/**
 * Клиент API групповой проценки (`/eplex/group_search/*`).
 *
 * Контракт сверен с:
 *  - Postman-коллекцией `src/tmp/gp/Eplex api.postman_collection (2).json` (раздел Group Search);
 *  - Расшифрованным BSL-кодом Eplex 1С (`src/tmp/gp/eplex_1c_decoded.md`,
 *    разделы 12–17). Особенности: `timeout: 60` отправляется всегда,
 *    `client_id` генерится клиентом (GUID), `/start` и `/stop` — GET без тела,
 *    `POST /report` принимает пустой массив `[]`.
 *
 * Используется analytics-целями модуля «Подбор» (competitors / monitoring /
 * pricing) — каждая цель ЛК мапится на серверный `mode` через
 * `mapGoalToMode()` в helpers/eplex/groupSearchMapping.ts.
 */

import instanceJWT from "./apiJwt";

// ── Типы режимов и payload-структур ───────────────────────────────────────

/** Серверные режимы групповой проценки. */
export type GsMode = "manual" | "tender" | "pricekeeper" | "retail";

/**
 * Параметры анализа. `max_time` ≥ 21 1С опускает из JSON (= «не ограничивать»).
 * `category` — подмножество {2,3,4,5}: 2=запрошенный, 3=оригиналы/замены,
 * 4=аналоги, 5=склады поставщика.
 */
export interface GsAnalyze {
  max_time?: number;
  category?: number[];
  cost_excess_percent?: number;
  order_by?: "cost" | "time" | "chance";
  brand_only?: boolean;
  article_only?: boolean;
  count_only?: boolean;
  delimiter?: string;
}

export interface GsNotify {
  email?: string;
  on_finish?: boolean;
  on_stop?: boolean;
}

export interface GsTrigger {
  type: "top_price" | "price_diff" | "min_offer_count" | "not_found";
  params?: { name: string; value: number }[];
}

export interface GsPricekeeperSettings {
  client_period: number;
  max_searches_per_day: number;
  use_price_from_site?: boolean;
  max_time?: number;
  triggers?: GsTrigger[];
  plugin_rules?: Record<string, number[]>;
  exception_rules?: Record<string, number[]>;
}

export interface GsTenderSettings {
  client_period: number;
  period_distribute: boolean;
  geolocation?: string;
  max_time?: number;
  plugin_filters?: Record<string, number[]>;
  report_type?: number;
  report_delivery?: GsReportDelivery[];
}

export interface GsRetailSettings {
  client_period: number;
  period_distribute: boolean;
  geolocation?: string;
  plugin_filters?: Record<string, number[]>;
}

export interface GsReportDelivery {
  type: "email" | "webhook";
  settings: {
    delivery_type?: "instant" | "on_finish" | "period";
    template?: number;
    http?: { method?: string; url: string };
  };
}

/**
 * Структура settings для PUT/POST `group_search`. Поля per-mode присутствуют
 * только когда `mode` совпадает (server-side не ругается на лишние, но 1С их
 * не шлёт — следуем поведению 1С для совместимости).
 */
export interface GsSettings {
  name: string;
  mode: GsMode;
  plugins: number[];
  notify: GsNotify;
  analyze: GsAnalyze;
  timeout?: number;
  ext_type?: string;
  ext_uuid?: string;
  ext_type_meta?: string;
  ext_name?: string;
  add_info?: Record<string, unknown>;
  manual?: Record<string, never>;
  pricekeeper?: GsPricekeeperSettings;
  tender?: GsTenderSettings;
  retail?: GsRetailSettings;
  report_type?: number;
  report_delivery?: GsReportDelivery[];
}

/** Корневой DTO одной групповой проценки (item в листинге и в `GET .../{id}`). */
export interface GsItemDto {
  request_id: string;
  client_id: string;
  date: number;
  started: number;
  status: number;
  last_row_id: number;
  next_row_id: number;
  estimate_time?: string;
  status_hint?: string;
  info?: {
    total: number;
    finished: number;
    in_progress: number;
    _min_interval?: number;
  };
  settings: GsSettings;
}

export interface GsListResponse {
  items: GsItemDto[];
  count: number;
}

/** Позиция группового поиска (row). `add_info` хранит привязку к ЛК-сущности. */
export interface GsRowSettings {
  max_cost?: number;
  required_count?: number;
  max_time?: number;
  plugins?: number[];
  cost_by_hash?: boolean;
}

export interface GsRowAddInfo {
  /** UUID/ID позиции в ЛК — нужно для двусторонней связи. */
  e0ProductID?: string;
  e0ext_uuid?: string;
  /** Произвольные пользовательские столбцы (значение строкой). */
  [extraKey: string]: string | number | boolean | null | undefined;
}

export interface GsRowInputItem {
  article: string;
  brand: string;
  comment?: string;
  settings?: GsRowSettings;
  add_info?: GsRowAddInfo;
}

export interface GsRowDto {
  id: number;
  article: string;
  brand: string;
  comment?: string;
  settings?: GsRowSettings;
  add_info?: GsRowAddInfo;
  status?: number;
  start_date?: number;
  end_date?: number;
  modify_date?: number;
  search_id?: string;
  /**
   * Per-row статистика после прогона. На реальном dev-стенде структура такая:
   *  - `summary.total_rows` / `total_rows_filtered` — общее количество найденных
   *    и оставшихся после фильтров предложений;
   *  - `plugins` — объект `{pluginId: {time_*, total_rows, total_rows_filtered}}`
   *    с per-plugin метриками;
   *  - `totalStarted / totalSuccess / totalError` — счётчики на уровне search.
   * (См. эксперимент 2026-06-01: `GET /group_search/.../rows` → `items[].stages.search`.)
   */
  stages?: {
    search?: {
      summary?: {
        total_rows?: number;
        total_rows_filtered?: number;
      };
      plugins?: Record<string, { total_rows?: number; total_rows_filtered?: number }>;
      totalStarted?: number;
      totalSuccess?: number;
      totalError?: number;
    };
  };
}

export interface GsRowsResponse {
  items: GsRowDto[];
}

/**
 * Одно предложение (оффер) в результатах позиции. Форма совпадает с элементом
 * результата одиночного поиска (`ResultItem`-совместимо) — это позволяет
 * переиспользовать `EplexSingleSearchResult` для рендера таблицы «Другое».
 * `cat`: 2=запрошенный, 3=оригиналы/замены, 4=аналоги, 5=склады поставщика.
 */
export interface GsOffer {
  plugin_id: string | number;
  article: string;
  brand: string;
  name?: string;
  cost: string | number;
  cat?: number;
  real_cat?: number;
  count?: string | number;
  time?: string;
  time_int?: number;
  time_orig?: number;
  city?: string;
  stock?: string | number;
  img?: string;
  pos_idx?: number;
  multiplicity?: string;
  mcount?: string;
  markers?: Record<string, unknown>;
  [extra: string]: unknown;
}

/**
 * Контент одной позиции: агрегаты `stages.search` плюс сами предложения в
 * `search_results.results`. `search_results` отсутствует, пока по позиции ещё
 * ничего не найдено. `time_from` — инкрементальный курсор дозагрузки.
 */
export interface GsRowContent extends GsRowDto {
  next_id?: number;
  search_results?: {
    time_from?: string;
    results?: GsOffer[];
  };
}

export interface GsAuthLimitsResponse {
  newsearch: { day: { max: number; cur: number }; hour: { max: number; cur: number } };
  gs_newsearch: { day: { max: number; cur: number }; hour: { max: number; cur: number } };
  gs_pricekeeper: { day: { max: number; cur: number }; hour: { max: number; cur: number } };
  retail: { day: { max: number; cur: number }; hour: { max: number; cur: number } };
}

// ── Обёртки envelope ──────────────────────────────────────────────────────

interface ApiEnvelope<T> {
  status: boolean;
  error: boolean | Record<string, unknown>;
  signature?: string;
  data: T;
}

/**
 * Сервер заворачивает payload дважды: внешний `{status, error, data}` и
 * внутренний `{status: "ok", error_code, ...rest}`. Возвращаем «rest».
 */
function unwrap<T>(envelope: ApiEnvelope<{ status?: string; error_code?: number } & T>): T {
  if (envelope.error && typeof envelope.error === "object") {
    const err = envelope.error as { message?: string };
    throw new Error(err.message ?? "API error");
  }
  const inner = envelope.data;
  if (inner && typeof inner === "object" && "status" in inner && inner.status === "err") {
    const err = inner as unknown as { err?: string };
    throw new Error(err.err ?? "Group search API error");
  }
  const { status: _s, error_code: _e, ...rest } = (inner ?? {}) as { status?: string; error_code?: number };
  return rest as T;
}

const BASE = "eplex/group_search";

// ── Список и одиночные ────────────────────────────────────────────────────

/**
 * Список всех групповых проценок профиля. Без параметров возвращает всё
 * (активные + завершённые). Опциональный `gs_type` — серверный фильтр:
 *  - `0` или `1` или omit — все доступные;
 *  - `2` — только активные/новые (внутренний фильтр 1С Eplex);
 *  - `3` — ничего (зарезервировано).
 *
 * Эмпирически проверено на dev-стенде. Не используйте `gs_type=2`, если нужны
 * завершённые проценки — backend их отфильтрует.
 */
export async function listGroupSearches(params: { gs_type?: number } = {}): Promise<GsListResponse> {
  const response = await instanceJWT.get(`${BASE}`, { params });
  return unwrap<GsListResponse>(response.data);
}

export async function getGroupSearch(requestId: string): Promise<{ item: GsItemDto }> {
  const response = await instanceJWT.get(`${BASE}/${requestId}`);
  return unwrap<{ item: GsItemDto }>(response.data);
}

/**
 * Создание новой групповой проценки.
 * `client_id` обязателен и генерируется клиентом (UUID v4), сохраняется в
 * нашей сущности и переиспользуется при сохранении / редактировании.
 */
export async function createGroupSearch(payload: {
  client_id: string;
  settings: GsSettings;
}): Promise<{ request_id: string; client_id: string }> {
  const body = {
    ...payload,
    settings: { timeout: 60, ...payload.settings },
  };
  const response = await instanceJWT.put(BASE, body);
  return unwrap<{ request_id: string; client_id: string }>(response.data);
}

export async function updateGroupSearch(
  requestId: string,
  payload: { client_id: string; settings: GsSettings },
): Promise<{ request_id: string; client_id: string }> {
  const body = {
    ...payload,
    settings: { timeout: 60, ...payload.settings },
  };
  const response = await instanceJWT.post(`${BASE}/${requestId}`, body);
  return unwrap<{ request_id: string; client_id: string }>(response.data);
}

export async function deleteGroupSearch(requestId: string): Promise<void> {
  await instanceJWT.delete(`${BASE}/${requestId}`);
}

// ── Управление запуском ───────────────────────────────────────────────────

/** Запустить процедуру. На сервере действительно GET без тела. */
export async function startGroupSearch(requestId: string): Promise<void> {
  await instanceJWT.get(`${BASE}/${requestId}/start`);
}

export async function stopGroupSearch(requestId: string): Promise<void> {
  await instanceJWT.get(`${BASE}/${requestId}/stop`);
}

// ── Журнал изменений ──────────────────────────────────────────────────────

export interface GsFlowsResponse {
  items: GsItemDto[];
  /** Серверный timestamp — использовать как `dfrom` в следующем запросе. */
  time?: string;
}

/**
 * Поток изменений. 1С опрашивает каждые 4–6 секунд, передавая в `dfrom`
 * значение `time` из предыдущего ответа.
 *
 * ВАЖНО: dfrom кодируем через encodeURIComponent (пробел → %20), а НЕ через
 * axios `params` (там пробел → «+»). Бэк не декодирует «+» как пробел и падает
 * с error_code 407 «Неверный параметр запроса: dfrom». Рабочий поллер корзины
 * (temp_cart/flows) кодирует так же.
 */
export async function getGroupSearchFlows(dfrom: string): Promise<GsFlowsResponse> {
  const response = await instanceJWT.get(`${BASE}/flows?dfrom=${encodeURIComponent(dfrom)}`);
  return unwrap<GsFlowsResponse>(response.data);
}

// ── Позиции (rows) ────────────────────────────────────────────────────────

/**
 * PUT (создать с клиентскими ключами 1, 2, ...) — используется при первой
 * пакетной загрузке позиций сразу после создания GS.
 */
export async function putGroupSearchRows(
  requestId: string,
  rows: GsRowInputItem[],
): Promise<{ info?: Record<string, number> }> {
  const items: Record<string, GsRowInputItem> = {};
  rows.forEach((row, idx) => {
    items[String(idx + 1)] = row;
  });
  const response = await instanceJWT.put(`${BASE}/${requestId}/rows`, { items });
  return unwrap<{ info?: Record<string, number> }>(response.data);
}

/** PATCH — частичное обновление: добавляет/обновляет/удаляет по необходимости. */
export async function patchGroupSearchRows(
  requestId: string,
  items: Record<string, GsRowInputItem>,
): Promise<{ info?: Record<string, number> }> {
  const response = await instanceJWT.patch(`${BASE}/${requestId}/rows`, { items });
  return unwrap<{ info?: Record<string, number> }>(response.data);
}

/**
 * Пакетная первичная загрузка позиций частями (до 10k): первый чанк — PUT
 * (создание набора с клиентскими ключами 1..k), последующие — PATCH со сквозными
 * ключами (k+1, k+2, …), т.к. повторный PUT может пересоздать весь набор, а PATCH
 * — штатный upsert. Для списка ≤ batchSize эквивалентно одиночному PUT.
 */
export async function putGroupSearchRowsBatched(
  requestId: string,
  rows: GsRowInputItem[],
  batchSize: number,
): Promise<void> {
  for (let offset = 0; offset < rows.length; offset += batchSize) {
    const slice = rows.slice(offset, offset + batchSize);
    if (offset === 0) {
      await putGroupSearchRows(requestId, slice);
      continue;
    }
    const items: Record<string, GsRowInputItem> = {};
    slice.forEach((row, i) => {
      items[String(offset + i + 1)] = row;
    });
    await patchGroupSearchRows(requestId, items);
  }
}

/** DELETE — удаление по серверным `id`. */
export async function deleteGroupSearchRows(requestId: string, ids: number[]): Promise<void> {
  await instanceJWT.delete(`${BASE}/${requestId}/rows`, { data: { items: ids } });
}

/** Получить все позиции с агрегированной статистикой `stages.search.summary`. */
export async function listGroupSearchRows(requestId: string): Promise<GsRowsResponse> {
  const response = await instanceJWT.get(`${BASE}/${requestId}/rows`);
  return unwrap<GsRowsResponse>(response.data);
}

export interface GsRowsFlowsResponse {
  items: GsRowDto[];
  /** Серверный timestamp — использовать как `dfrom` в следующем запросе. */
  time?: string;
}

/**
 * Дельта изменившихся ПОЗИЦИЙ (rows) группового поиска после `dfrom`
 * (`GET group_search/{id}/flows?dfrom=`). Возвращает только строки с обновлённой
 * статистикой поиска — на порядки легче полного `listGroupSearchRows` при живом
 * обновлении статусов проценки на больших списках (10k+). dfrom кодируем
 * encodeURIComponent (пробел → %20), см. `getGroupSearchFlows`.
 */
export async function getGroupSearchRowsFlows(requestId: string, dfrom: string): Promise<GsRowsFlowsResponse> {
  const response = await instanceJWT.get(`${BASE}/${requestId}/flows?dfrom=${encodeURIComponent(dfrom)}`, {
    _silent: true,
  } as never);
  return unwrap<GsRowsFlowsResponse>(response.data);
}

/**
 * Контент одной позиции: `GET group_search/{id}/{rowId}` возвращает агрегаты
 * `stages.search` и сами предложения в `search_results.results` (форма как в
 * одиночном поиске). Опциональный `timeFrom` — курсор дозагрузки; кодируем
 * через encodeURIComponent (пробел → %20), см. `getGroupSearchFlows`.
 */
export async function getGroupSearchRowContent(
  requestId: string,
  rowId: number,
  timeFrom?: string,
  options?: { silent?: boolean },
): Promise<{ item: GsRowContent }> {
  const url = timeFrom
    ? `${BASE}/${requestId}/${rowId}?time_from=${encodeURIComponent(timeFrom)}`
    : `${BASE}/${requestId}/${rowId}`;
  // Выгрузка читает строки пачкой: глобальный тост на каждую неудачу превратился
  // бы в очередь одинаковых сообщений, поэтому там запросы тихие (CAB-727).
  const response = options?.silent
    ? await instanceJWT.get(url, { _silent: true } as never)
    : await instanceJWT.get(url);
  return unwrap<{ item: GsRowContent }>(response.data);
}

// ── Отчёт ─────────────────────────────────────────────────────────────────

/**
 * Триггер генерации отчёта. Тело — пустой массив (это намеренно, проверено
 * через 1С). Возвращает текст уведомления (отправляется на e-mail из настроек).
 */
export async function triggerGroupSearchReport(requestId: string): Promise<{ msg?: string }> {
  const response = await instanceJWT.post(`${BASE}/${requestId}/report`, []);
  return unwrap<{ msg?: string }>(response.data);
}

// ── Estimate ──────────────────────────────────────────────────────────────

export interface GsEstimatePayload {
  total: number;
  settings: GsSettings;
}

/**
 * Предварительная оценка времени выполнения. Сервер вернёт строку вида
 * "~ 1 минуту" в поле `message`.
 */
export async function estimateGroupSearch(payload: GsEstimatePayload): Promise<{ message?: string }> {
  const body = { ...payload, settings: { timeout: 60, ...payload.settings } };
  const response = await instanceJWT.post(`${BASE}/estimate`, body);
  const envelope = response.data as ApiEnvelope<{
    status?: string;
    error_code?: number;
    message?: string;
  }>;
  if (envelope.error && typeof envelope.error === "object") {
    const err = envelope.error as { message?: string };
    throw new Error(err.message ?? "Estimate API error");
  }
  return { message: envelope.data.message };
}

// ── Лимиты ────────────────────────────────────────────────────────────────

/**
 * Лицензионные лимиты. `gs_newsearch` отвечает за manual/tender,
 * `gs_pricekeeper` за pricekeeper, `retail` за retail-режим,
 * `newsearch` — одиночный поиск (не наш кейс).
 */
export async function getAuthLimits(): Promise<GsAuthLimitsResponse> {
  const response = await instanceJWT.get("eplex/auth/limits");
  return unwrap<GsAuthLimitsResponse>(response.data);
}
